import { type ContactData, type ContactListSuccess, type ContactRemoveInput } from '@terros/common'
import { type InfiniteData, type QueryClient, useQueryClient } from '@tanstack/react-query'

type ContactCache = {
  removeItem: (input: ContactRemoveInput) => Promise<void>
}

const matchesContact = (contact: ContactData, input: ContactRemoveInput): boolean => {
  return contact.pk === input.pk && contact.sk === input.sk
}

export const removeContactFromCache = async (queryClient: QueryClient, input: ContactRemoveInput): Promise<void> => {
  await Promise.all([
    queryClient.cancelQueries({ queryKey: ['contacts'] }),
    queryClient.cancelQueries({ queryKey: ['contacts-infinite'] }),
  ])

  queryClient.setQueriesData<ContactData[]>({ queryKey: ['contacts'] }, (old) => {
    return old?.filter((contact) => !matchesContact(contact, input))
  })
  queryClient.setQueriesData<InfiniteData<ContactListSuccess>>({ queryKey: ['contacts-infinite'] }, (old) => {
    if (!old?.pages.some((page) => page.contacts.some((contact) => matchesContact(contact, input)))) return old

    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        contacts: page.contacts.filter((contact) => !matchesContact(contact, input)),
        total: page.total === undefined ? undefined : Math.max(0, page.total - 1),
      })),
    }
  })
}

export function useContactCache(): ContactCache {
  const queryClient = useQueryClient()
  return {
    removeItem: (input: ContactRemoveInput): Promise<void> => removeContactFromCache(queryClient, input),
  }
}
